import React, { useEffect } from 'react';
import { Card, Button, Tag, Tooltip } from 'antd';
import { CloudOutlined, CloudSyncOutlined, CheckCircleOutlined } from '@ant-design/icons';
import DataTable, { DataTableColumn } from '../../components/DataTable/DataTable';
import EventFormModal from '../../components/Events/EventFormModal';
import useEventStore from '../../store/eventStore';
import { EventModel } from '../../models/events/events';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModalUtil from '../../store/core/modalUtil';
import { useCachedEventIds, usePreloadEventData, useOnlineStatus, useAutoEventCacheManager } from '../../offlinestore/useOfflineSync';
import { useAuthStore } from '../../store/authUserStore';
import { UserRole } from '../../models/users';

const Events: React.FC = () => {
  const { items, fetchItems, deleteItem, setSelectedItem } = useEventStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cachedIds, refresh: refreshCachedIds } = useCachedEventIds();
  const { isPreloading } = usePreloadEventData();
  const isOnline = useOnlineStatus();
  const { runCacheManager } = useAutoEventCacheManager();
  const { currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === UserRole.ADMIN;

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Trigger auto-cache check on every visit to the events page
  useEffect(() => {
    if (isOnline) {
      runCacheManager().then(() => refreshCachedIds());
    }
  }, [isOnline, runCacheManager, refreshCachedIds]);

  const handleRowClick = (event: EventModel) => {
    navigate(`/events/details/${event.id}`);
  };

  const handleEdit = (eventId: string) => {
    const item = items.find((x) => x.id === eventId);
    if (item) {
      const newSelectedEvent = { ...item, userId: item.user?.id! };
      setSelectedItem({ ...item, userId: item.user?.id! });

      ModalUtil.openModal(<EventFormModal initialValues={newSelectedEvent} />, t('events.editEvent'), "50%");
    }
  }

  const columns: DataTableColumn<EventModel>[] = [
    { header: t('general.name'), accessor: 'name' },
    { header: t('events.description'), accessor: 'description' },
    { header: t('events.accountant'), accessor: 'user.username' },
    { header: t('events.startDate'), accessor: 'startDate', type: 'date' },
    { header: t('events.endDate'), accessor: 'endDate', type: 'date' },
    { header: t('events.status'), accessor: 'status', renderCell: (item) => t(`events.${item.status}`), hideFilter: true },
    {
      header: <span data-tour="events-offline-column">{t('general.offline')}</span>,
      accessor: 'id',
      hideFilter: true,
      renderCell: (item) => {
        const isCached = cachedIds.has(item.id);
        if (isCached) {
          return (
            <Tooltip title={t('general.eventCached')}>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {t('general.offline')}
              </Tag>
            </Tooltip>
          );
        }
        if (isPreloading) {
          return (
            <Tooltip title={t('general.cachingEvent')}>
              <Tag color="processing" icon={<CloudSyncOutlined />}>
                {t('general.syncing')}
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={t('general.dataMayNotBeCached')}>
            <Tag color="default" icon={<CloudOutlined />}>
              -
            </Tag>
          </Tooltip>
        );
      }
    },
  ];

  return (
    <div data-tour="events-list">
      <Card
        title={t('events.title')}
        extra={
          isAdmin && (
            <Button type="primary" onClick={() => {
              ModalUtil.openModal(<EventFormModal initialValues={null} />, t('events.createEvent'), "50%")
            }}>
              {t('events.createEvent')}
            </Button>
          )
        }
      >
        <DataTable
          columns={columns}
          data={items}
          onDelete={isAdmin ? deleteItem : undefined}
          onRowClick={handleRowClick}
          onEdit={isAdmin ? handleEdit : undefined}
          enableFrontendFiltering
        />
      </Card>
    </div>
  );
};

export default Events;
